var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import * as React from 'react';
import { Modal } from '@doordash/component-modal';
import { useToast } from '@doordash/component-toast';
import { Icon, Inset } from '@doordash/design-language';
import track from '../../utils/track';
import AddMembersForm from './add_members_form';
import { EmployeesEvents } from '../../constants/events';
import { useAddTeamMembersToTeamAccountMutation } from './useAddTeamMembersToTeamAccountMutation';
import { AddTeamMemberToTeamAccountErrorCode } from '../../types/GloblalTypes';
import MembersStrings from './MembersStrings';
import InformCannotAddEmailContent from './inform_cannot_add_email_content';
var errorStrings = MembersStrings.addMemberError, addSuccessToastStrings = MembersStrings.addSuccessToast;
export var getArrayFromMemberList = function (inputString) {
    if (inputString && inputString.trim()) {
        return inputString.trim().split(/[\s,]+/);
    }
    return [];
};
export var ModalSteps;
(function (ModalSteps) {
    ModalSteps["ADD_MEMBERS_FORM"] = "ADD_MEMBERS_FORM";
    ModalSteps["INFORM_CANNOT_ADD_EMAIL"] = "INFORM_CANNOT_ADD_EMAIL";
    ModalSteps["CLOSE"] = "CLOSE";
})(ModalSteps || (ModalSteps = {}));
export var AddMembersModal = function (_a) {
    var teamAccountId = _a.teamAccountId, onClose = _a.onClose, onMemberAdded = _a.onMemberAdded, maxMembersLimit = _a.maxMembersLimit, totalMembersCount = _a.totalMembersCount;
    var displayToast = useToast().displayToast;
    var _b = __read(React.useState({}), 2), formResponse = _b[0], setFormResponse = _b[1];
    var _c = __read(React.useState(false), 2), showLimitationErrorBanner = _c[0], setShowLimitationErrorBanner = _c[1];
    var addTeamMembersToTeamAccount = useAddTeamMembersToTeamAccountMutation().addTeamMembersToTeamAccount;
    var _d = __read(React.useState(''), 2), title = _d[0], setTitle = _d[1];
    var _e = __read(React.useState(0), 2), addSuccessCount = _e[0], setAddSuccessCount = _e[1];
    var _f = __read(React.useState(ModalSteps.ADD_MEMBERS_FORM), 2), modalStep = _f[0], setModalStep = _f[1];
    var _g = __read(React.useState([]), 2), addToTeamAccountErrors = _g[0], setAddToTeamAccountErrors = _g[1];
    React.useEffect(function () {
        var displayAddMembersSuccessToast = function (numMembers) {
            displayToast({
                icon: Icon.Types.Check,
                text: numMembers === 1
                    ? addSuccessToastStrings.singleAdd()
                    : addSuccessToastStrings.multipleAdd(numMembers),
                insetFromEdge: Inset.Sizes.XxxLarge,
            });
        };
        var trackSubmitAddMembers = function (tAccountId, numMembers) {
            track(EmployeesEvents.SUBMIT_ADD_MEMBERS, {
                member_emails_count: numMembers,
                team_account_id: tAccountId,
            });
        };
        if (modalStep === ModalSteps.CLOSE) {
            trackSubmitAddMembers(teamAccountId, addSuccessCount);
            displayAddMembersSuccessToast(addSuccessCount);
            onMemberAdded();
            onClose();
        }
    }, [
        modalStep,
        addSuccessCount,
        teamAccountId,
        onClose,
        onMemberAdded,
        displayToast,
    ]);
    var handleFormSubmit = function (values, actions) {
        var membersArray = getArrayFromMemberList(values.members);
        var numEmails = membersArray.length;
        if (Boolean(maxMembersLimit) &&
            totalMembersCount + numEmails > maxMembersLimit) {
            setShowLimitationErrorBanner(true);
            actions.setSubmitting(false);
            return;
        }
        submitUsingGql(teamAccountId, membersArray, actions);
    };
    var submitUsingGql = function (tAccountId, emails, formActions) {
        addTeamMembersToTeamAccount(tAccountId, emails, {
            onCompleted: function (response) {
                var _a = response.addTeamMembersToTeamAccount, validationError = _a.validationError, errors = _a.errors;
                var isPayloadExceeded = validationError === null || validationError === void 0 ? void 0 : validationError.payloadSizeError;
                var isTeamAccountPlanLimitReached = !!(errors === null || errors === void 0 ? void 0 : errors.find(function (error) {
                    return error.code ===
                        AddTeamMemberToTeamAccountErrorCode.ADD_TEAM_MEMBER_TO_TEAM_ACCOUNT_ERROR_CODE_MEMBERS_PER_TEAM_ACCOUNT_PLAN_LIMIT_EXCEEDED;
                }));
                var emailsWithError = errors === null || errors === void 0 ? void 0 : errors.filter(function (error) { return !!error.email; });
                formActions.setSubmitting(false);
                if (isPayloadExceeded) {
                    var payloadLimit = validationError.payloadSizeError.limit;
                    var numEmailsToRemove = emails.length - payloadLimit;
                    var exceedLimitErrorMessage = numEmailsToRemove > 1
                        ? errorStrings.exceedEmailLimit.multipleFail(payloadLimit, numEmailsToRemove)
                        : errorStrings.exceedEmailLimit.singleFail(payloadLimit, numEmailsToRemove);
                    setFormResponse({
                        response: {
                            success: false,
                            errorMessage: errorStrings.default() + " " + exceedLimitErrorMessage,
                        },
                    });
                    return;
                }
                if (isTeamAccountPlanLimitReached) {
                    // this should not be reached because we already have a check before submitting but handle error anyways
                    // team account max limit reached
                    setShowLimitationErrorBanner(true);
                    return;
                }
                if (!!(emailsWithError === null || emailsWithError === void 0 ? void 0 : emailsWithError.length)) {
                    // emails have errors, show error modal
                    var successCount = emails.length - emailsWithError.length;
                    setAddSuccessCount(successCount);
                    setAddToTeamAccountErrors(emailsWithError);
                    setModalStep(ModalSteps.INFORM_CANNOT_ADD_EMAIL);
                    return;
                }
                // no errors
                setAddSuccessCount(emails.length);
                setModalStep(ModalSteps.CLOSE);
            },
            onError: function () {
                setFormResponse({
                    response: {
                        success: false,
                        errorMessage: errorStrings.default(),
                    },
                });
                formActions.setSubmitting(false);
            },
        });
    };
    return (React.createElement(Modal, { onClose: onClose, title: title }, modalStep === ModalSteps.ADD_MEMBERS_FORM ? (React.createElement(AddMembersForm, { setTitle: setTitle, handleFormSubmit: handleFormSubmit, formResponse: formResponse, onClose: onClose, maxMembersLimit: maxMembersLimit, totalMembersCount: totalMembersCount, showLimitationErrorBanner: showLimitationErrorBanner, teamAccountId: teamAccountId })) : modalStep === ModalSteps.INFORM_CANNOT_ADD_EMAIL ? (React.createElement(InformCannotAddEmailContent, { setTitle: setTitle, errors: addToTeamAccountErrors, onOk: function () { return setModalStep(ModalSteps.CLOSE); } })) : null));
};
export default AddMembersModal;
